/**
 * Sticky Product Sidebar
 *
 * Manages sticky behavior for product details sidebar on desktop.
 * Uses a class-based state machine with automatic layout detection.
 */

import { StickyProductController } from './StickyProductController.js';

// Global controller instance
let stickyController = null;

/**
 * Initialize or reinitialize the sticky product controller
 */
function initializeStickyProduct() {
	// Destroy existing instance if present
	if (stickyController) {
		stickyController.destroy();
		stickyController = null;
	}

	// Create and initialize new controller
	stickyController = new StickyProductController();
	stickyController.init();
}

/**
 * Reinitialize with double RAF for layout settling
 */
function reinitialize() {
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			initializeStickyProduct();
		});
	});
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
	initializeStickyProduct();
});

// Handle viewport resize (mobile ↔ desktop transitions)
let lastViewportWasDesktop = window.innerWidth > 768;

window.addEventListener('resize', () => {
	const isDesktop = window.innerWidth > 768;

	// Mobile → Desktop: Initialize if not already active
	if (!lastViewportWasDesktop && isDesktop) {
		if (!stickyController || !stickyController.isActive) {
			initializeStickyProduct();
		}
	}

	// Desktop → Mobile: Destroy and clean up
	if (lastViewportWasDesktop && !isDesktop) {
		if (stickyController) {
			stickyController.destroy();
			stickyController = null;
		}
	}

	lastViewportWasDesktop = isDesktop;
});

// Smart reinitialization when filtered product list rerenders
// Only do full reinit if the changed section is the active stop marker
document.addEventListener('filtered-product-list:loaded', (event) => {
	const changedSectionId = event.detail?.sectionId;

	// Check if this section is the one we're using as stop marker
	if (stickyController && stickyController.isActive) {
		const isActiveStopSection = stickyController.activeStopSectionId === changedSectionId;

		if (isActiveStopSection) {
			// Stop marker section changed - need full reinit
			reinitialize();
		} else {
			// Other section loaded - just invalidate layout measurements
			// This recalculates container/stop positions while preserving
			// the stable stickyOffsetFromContainer value
			stickyController.invalidateLayout();
		}
	}
});
